import React, { useState, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { withTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Input, Table, Select, Button, Tag, Spin, Alert } from 'antd'

import companyActions from '../../redux/customers/companies/actions'
import CompanyFormContainer from './Companies/CompanyFormContainer'
import AddContactModal from './dashboard/AddContactModal'
import AddCompanyModal from './dashboard/AddCompanyModal'
import {
    CUSTOMER_DASHBOARD_QUERY,
    SEARCH_CUSTOMERS_COMPANIES_QUERY
} from './dashboard/queries'
import {
    compactBahtFromSatangs,
    compactBahtFromValue,
    PROJECT_STATUS_LABELS,
    PROJECT_STATUS_COLORS
} from './dashboard/format'
import { SEGMENT_COLORS, SEGMENT_LABELS } from './CustomerLifetimeValue/format'
import CustomersIndexWrapper from './customersIndex.style'

const SEGMENT_OPTIONS = [
    { value: 'all', label: 'กลุ่ม CLV : ทั้งหมด' },
    { value: 'vip', label: SEGMENT_LABELS.vip },
    { value: 'high', label: SEGMENT_LABELS.high },
    { value: 'medium', label: SEGMENT_LABELS.medium },
    { value: 'low', label: SEGMENT_LABELS.low },
    { value: 'new_customer', label: SEGMENT_LABELS.new_customer },
    { value: 'at_risk', label: SEGMENT_LABELS.at_risk },
    { value: 'churned', label: SEGMENT_LABELS.churned },
    { value: 'unsegmented', label: SEGMENT_LABELS.unsegmented }
]

const segmentTag = (segment) => {
    const key = segment || 'unsegmented'
    return <Tag color={SEGMENT_COLORS[key]}>{SEGMENT_LABELS[key]}</Tag>
}


// Wrap every case-insensitive occurrence of `term` in `text` with a <mark>.
// Returns the plain string when there is no term or no match.
const highlight = (text, term) => {
    const str = text == null ? '' : String(text)
    if (!term) return str
    const lower = str.toLowerCase()
    const needle = term.toLowerCase()
    if (!lower.includes(needle)) return str

    const parts = []
    let i = 0
    let key = 0
    let found = lower.indexOf(needle, i)
    while (found !== -1) {
        if (found > i) parts.push(str.slice(i, found))
        parts.push(<mark className="hl" key={key++}>{str.slice(found, found + needle.length)}</mark>)
        i = found + needle.length
        found = lower.indexOf(needle, i)
    }
    if (i < str.length) parts.push(str.slice(i))
    return parts
}

const CustomersIndexContainer = () => {
    // `inputValue` is what's typed in the box; `query` is the committed search
    // term that actually drives the GraphQL search. They're split so the search
    // only fires on Enter (or the search-icon click), not on every keystroke.
    const [inputValue, setInputValue] = useState('')
    const [query, setQuery] = useState('')
    const [segment, setSegment] = useState('all')
    // "No company" group is capped and revealed 5 at a time.
    const NO_COMPANY_PAGE = 5
    const [noCompanyLimit, setNoCompanyLimit] = useState(NO_COMPANY_PAGE)

    // Company whose "เพิ่มผู้ติดต่อ" modal is open (null = closed).
    const [contactCompany, setContactCompany] = useState(null)
    // Customer whose "เพิ่มบริษัท" modal is open (null = closed).
    const [companyCustomer, setCompanyCustomer] = useState(null)

    const dispatch = useDispatch()
    const companyModalOpen = useSelector((state) => state.getIn(['customerCompanies', 'openAddcompanyModal']))

    const trimmed = query.trim()
    const isSearch = trimmed.length > 0

    const dashboard = useQuery(CUSTOMER_DASHBOARD_QUERY, {
        variables: { clvSegment: segment === 'all' ? null : segment }
    })

    const searchResult = useQuery(SEARCH_CUSTOMERS_COMPANIES_QUERY, {
        variables: { query: trimmed },
        skip: !isSearch
    })

    // Reset the "no company" reveal count whenever the search term changes.
    useEffect(() => { setNoCompanyLimit(NO_COMPANY_PAGE) }, [trimmed])

    const openAddCompany = () => dispatch(companyActions.setAddCompanyModal(true))
    // A new company only surfaces in the grouped search view; refetch it if active.
    const handleCompanyCreated = () => { if (isSearch) searchResult.refetch() }
    // Refetch the grouped search so the new contact appears under its company.
    const handleContactAdded = () => { if (isSearch) searchResult.refetch() }
    // Refresh the active view so the customer's new company shows immediately.
    const handleCompanyAdded = () => { if (isSearch) searchResult.refetch(); else dashboard.refetch() }

    const staff = dashboard.data && dashboard.data.currentStaff
    const holdings = useMemo(() => {
        const rows = (staff && staff.customerHoldings) || []
        return rows.slice().sort((a, b) => Number(b.predictedClvSatangs) - Number(a.predictedClvSatangs))
    }, [staff])

    // bookHealths returns one entry per team; prefer the sale book, else the first.
    const book = useMemo(() => {
        const list = (staff && staff.bookHealths) || []
        return list.find((b) => b.team === 'sale') || list[0] || null
    }, [staff])

    const companyCount = useMemo(() => {
        const ids = new Set()
        holdings.forEach((h) => (h.customer.companies || []).forEach((c) => ids.add(c.id)))
        return ids.size
    }, [holdings])

    // ----- shared cell renderers -----
    // Secondary contact line: phone · email · Line. When the customer has a
    // lineUrl, the Line part becomes a link that opens the chat in a new tab.
    const contactMetaNode = (customer) => {
        const parts = []
        if (customer.phoneNumber) parts.push(highlight(customer.phoneNumber, trimmed))
        if (customer.email) parts.push(highlight(customer.email, trimmed))
        if (customer.lineId || customer.lineUrl) {
            const label = `Line: ${customer.lineId || ''}`.trim()
            parts.push(
                customer.lineUrl ? (
                    <a href={customer.lineUrl} target="_blank" rel="noopener noreferrer">
                        {highlight(label, trimmed)}
                    </a>
                ) : highlight(label, trimmed)
            )
        }
        if (!parts.length) return '-'
        return parts.map((p, i) => (
            <React.Fragment key={i}>{i > 0 && ' · '}{p}</React.Fragment>
        ))
    }

    const nameCell = (customer, seg) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Link to={`/customers/${customer.customerNo}`}>{highlight(customer.fullName || customer.customerNo, trimmed)}</Link>
                {segmentTag(seg)}
            </div>
            <span className="cell-secondary">{contactMetaNode(customer)}</span>
        </div>
    )

    const money = (satangs) => <span className="num">{compactBahtFromSatangs(satangs)}</span>

    const daysCell = (d) => {
        if (d == null) return <span className="num">-</span>
        const overdue = d >= 90
        return (
            <span className="num" style={{ fontWeight: overdue ? 600 : 400, color: overdue ? '#cf1322' : undefined }}>
                {d}
            </span>
        )
    }

    // `withAddCompany` is passed in the grouped search view, where there's no
    // company column to host the button (the holdings table has it there instead).
    const actionCell = (customer, withAddCompany = false) => (
        <div style={{ display: 'flex', gap: 16, whiteSpace: 'nowrap' }}>
            <Link to={`/customers/${customer.customerNo}/orders/new`}>เพิ่มใบเสนอราคา</Link>
            <Link to={`/customers/${customer.customerNo}/edit`}>แก้ไข</Link>
            {withAddCompany && <a onClick={() => setCompanyCustomer(customer)}>เพิ่มบริษัท</a>}
        </div>
    )

    const clvCol = { title: 'Predicted CLV', key: 'clv', width: 120, align: 'right' }
    const revCol = { title: 'Lifetime Rev', key: 'rev', width: 115, align: 'right' }
    const q90Col = { title: 'Q90 Rev', key: 'q90', width: 100, align: 'right' }
    const daysCol = { title: 'วันนับจากออเดอร์', key: 'days', width: 140, align: 'right' }
    const actCol = { title: 'ดำเนินการ', key: 'act', width: 200 }

    // ----- default (holdings) table -----
    const holdingsColumns = [
        { title: 'ชื่อลูกค้า', key: 'name', render: (r) => nameCell(r.customer, r.clvSegment) },
        {
            title: 'บริษัท',
            key: 'co',
            width: 220,
            render: (r) => {
                const cos = r.customer.companies || []
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                        {cos.map((c) => (
                            <span key={c.id} style={{ fontSize: 13 }}>{c.name}</span>
                        ))}
                        <a style={{ fontSize: 12 }} onClick={() => setCompanyCustomer(r.customer)}>+ เพิ่มบริษัท</a>
                    </div>
                )
            }
        },
        { ...clvCol, render: (r) => money(r.predictedClvSatangs) },
        { ...revCol, render: (r) => money(r.lifetimeRevenueSatangs) },
        { ...q90Col, render: (r) => money(r.q90RevenueSatangs) },
        { ...daysCol, render: (r) => daysCell(r.daysSinceLastOrder) },
        { ...actCol, render: (r) => actionCell(r.customer) }
    ]

    // ----- search view: group by company -----
    const groups = useMemo(() => {
        const data = searchResult.data
        if (!data) return []
        const customers = (data.searchCustomers && data.searchCustomers.nodes) || []
        const companies = data.searchCompanies || []

        const filteredCustomers = customers.filter(
            (c) => segment === 'all' || (c.performance && c.performance.clvSegment === segment)
        )

        const meta = new Map()
        companies.forEach((co) => meta.set(co.id, { taxId: co.taxId, address: co.addressDetail, projects: co.projects || [] }))

        const byId = new Map()
        const order = []
        const ensure = (id, name) => {
            if (!byId.has(id)) {
                const m = meta.get(id) || {}
                byId.set(id, {
                    key: id, id, name, isCompany: true,
                    taxId: m.taxId, address: m.address, projects: m.projects || [], contacts: []
                })
                order.push(id)
            }
            return byId.get(id)
        }

        // Show company-hit groups (even with no matched contacts) only when unfiltered.
        if (segment === 'all') companies.forEach((co) => ensure(co.id, co.name))

        const noCompany = { key: '__none__', name: 'ไม่สังกัดบริษัท', isCompany: false, contacts: [] }
        filteredCustomers.forEach((cu) => {
            const cos = cu.companies || []
            if (!cos.length) {
                noCompany.contacts.push(cu)
            } else {
                cos.forEach((co) => ensure(co.id, co.name).contacts.push(cu))
            }
        })

        const result = []
        if (noCompany.contacts.length) result.push(noCompany)
        order.forEach((id) => result.push(byId.get(id)))
        return result
    }, [searchResult.data, segment])

    // "เพิ่มบริษัท" only makes sense for contacts not yet assigned to a company
    // (the "ไม่สังกัดบริษัท" group); within a company group the contact already belongs to it.
    const groupColumns = (withAddCompany) => [
        { title: 'ชื่อลูกค้า', key: 'name', render: (c) => nameCell(c, c.performance && c.performance.clvSegment) },
        { ...clvCol, render: (c) => money(c.performance && c.performance.predictedClvSatangs) },
        { ...revCol, render: (c) => money(c.performance && c.performance.historicalRevenueClvSatangs) },
        { ...daysCol, render: (c) => daysCell(c.performance ? c.performance.daysSinceLastOrder : null) },
        { ...actCol, width: withAddCompany ? 280 : 200, render: (c) => actionCell(c, withAddCompany) }
    ]

    const renderStats = () => {
        if (!book) return null
        const tiles = [
            { label: 'ลูกค้าที่ดูแล', value: book.customersHeld },
            { label: 'Active', value: book.customersActive, color: '#389e0d' },
            { label: 'At risk', value: book.customersAtRisk, color: '#d48806' },
            { label: 'Churned', value: book.customersChurned, color: '#cf1322' },
            { label: 'Book CLV', value: compactBahtFromSatangs(book.activeBookClvSatangs), color: '#389e0d' },
            { label: 'บริษัท', value: companyCount }
        ]
        return (
            <div className="stats-grid">
                {tiles.map((t) => (
                    <div className="stat-tile" key={t.label}>
                        <div className="stat-label">{t.label}</div>
                        <div className="stat-value" style={t.color ? { color: t.color } : undefined}>{t.value}</div>
                    </div>
                ))}
            </div>
        )
    }

    const renderHoldings = () => (
        <div className="list-section">
            <div className="section-head">
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span className="section-title">Customer Holdings ({holdings.length})</span>
                    <span className="section-hint">เรียงตาม Predicted CLV</span>
                </div>
                <span className="section-hint">พิมพ์ค้นหาเพื่อดูมุมมองจัดกลุ่มตามบริษัท</span>
            </div>
            <Table
                columns={holdingsColumns}
                dataSource={holdings}
                rowKey="id"
                size="small"
                loading={dashboard.loading}
                pagination={false}
                scroll={{ x: 1150 }}
            />
        </div>
    )

    const renderProjects = (projects) => (
        <div className="projects-row">
            <span className="projects-label">โปรเจกต์</span>
            {projects.map((p) => (
                <span className="project-chip" key={p.id}>
                    <span>{highlight(p.name, trimmed)}</span>
                    {p.status && <Tag color={PROJECT_STATUS_COLORS[p.status]} style={{ margin: 0 }}>{PROJECT_STATUS_LABELS[p.status]}</Tag>}
                    <span className="cell-secondary">{compactBahtFromValue(p.paidPrice && p.paidPrice.value)}</span>
                </span>
            ))}
        </div>
    )

    const renderGroups = () => {
        if (searchResult.loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />
        if (searchResult.error) return <Alert type="error" message={searchResult.error.message} />
        if (!groups.length) return <div className="no-results">ไม่พบข้อมูลที่ตรงกับการค้นหา</div>

        return (
            <div className="groups">
                {groups.map((g) => (
                    <div className="list-section" key={g.key}>
                        <div className="group-head">
                            <div style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <span className="section-title">{highlight(g.name, trimmed)}</span>
                                    <span className="group-count">
                                        {g.isCompany
                                            ? `${g.contacts.length} ผู้ติดต่อ · ${(g.projects || []).length} โปรเจกต์`
                                            : `${g.contacts.length} ราย`}
                                    </span>
                                </div>
                                {g.isCompany && (g.taxId || g.address) && (
                                    <div className="group-meta">
                                        {highlight([g.taxId ? `เลขประจำตัวผู้เสียภาษี: ${g.taxId}` : '', g.address].filter(Boolean).join(' · '), trimmed)}
                                    </div>
                                )}
                            </div>
                            {g.isCompany && (
                                <div className="group-actions">
                                    <a onClick={() => setContactCompany({ id: g.id, name: g.name })}>+ เพิ่มผู้ติดต่อ</a>
                                    <Link to={`/companies/${g.id}`}>ดูบริษัท</Link>
                                </div>
                            )}
                        </div>
                        {g.isCompany && (g.projects || []).length > 0 && renderProjects(g.projects)}
                        {g.contacts.length > 0 ? (
                            <React.Fragment>
                                <Table
                                    columns={groupColumns(!g.isCompany)}
                                    dataSource={g.isCompany ? g.contacts : g.contacts.slice(0, noCompanyLimit)}
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    scroll={{ x: 1080 }}
                                />
                                {!g.isCompany && g.contacts.length > noCompanyLimit && (
                                    <div style={{ textAlign: 'center', padding: '10px 16px' }}>
                                        <Button
                                            type="link"
                                            onClick={() => setNoCompanyLimit((n) => n + NO_COMPANY_PAGE)}
                                        >
                                            โหลดเพิ่มเติม ({g.contacts.length - noCompanyLimit})
                                        </Button>
                                    </div>
                                )}
                            </React.Fragment>
                        ) : (
                            <div className="group-empty">ยังไม่มีผู้ติดต่อ</div>
                        )}
                    </div>
                ))}
            </div>
        )
    }

    return (
        <CustomersIndexWrapper>
            <div className="dashboard-card">
                <div className="dashboard-header">
                    <div>
                        <div className="dashboard-title">ลูกค้าและบริษัท</div>
                        <div className="dashboard-subtitle">
                            {staff && staff.fullName ? `Customer Holdings ของ ${staff.fullName} · ` : ''}
                            ข้อมูล ณ {dayjs().format('D MMM YYYY')}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button onClick={openAddCompany}>+ เพิ่มบริษัท</Button>
                        <Link to="/customers/new"><Button type="primary">+ เพิ่มลูกค้า</Button></Link>
                    </div>
                </div>

                {dashboard.error && <Alert type="error" style={{ marginBottom: 20 }} message={dashboard.error.message} />}

                {renderStats()}

                <div className="controls-row">
                    <div className="search">
                        <Input.Search
                            placeholder="ค้นหา (ชื่อ, เบอร์โทร, อีเมล, ไลน์, เลขภาษี, โปรเจกต์)"
                            allowClear
                            size="large"
                            value={inputValue}
                            onChange={(e) => {
                                const v = e.target.value
                                setInputValue(v)
                                // Clearing the box returns to the holdings view immediately.
                                if (!v) setQuery('')
                            }}
                            onSearch={(value) => setQuery(value)}
                        />
                    </div>
                    <Select
                        size="large"
                        style={{ width: 200 }}
                        value={segment}
                        onChange={setSegment}
                        options={SEGMENT_OPTIONS}
                    />
                </div>

                {isSearch ? renderGroups() : renderHoldings()}
            </div>

            <CompanyFormContainer
                visible={!!companyModalOpen}
                title="เพิ่มบริษัท"
                onSaveSuccess={handleCompanyCreated}
            />

            <AddContactModal
                company={contactCompany}
                visible={!!contactCompany}
                onClose={() => setContactCompany(null)}
                onSuccess={handleContactAdded}
            />

            <AddCompanyModal
                customer={companyCustomer}
                visible={!!companyCustomer}
                onClose={() => setCompanyCustomer(null)}
                onSuccess={handleCompanyAdded}
            />
        </CustomersIndexWrapper>
    )
}

export default withTranslation('common')(CustomersIndexContainer)
